import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap, AttributionControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Strumento "Vicine": trova le attivita' vicine (Google Places) dalla posizione GPS e,
// da ognuna, lancia le verifiche (456 sul telefono, Dominio sul sito).
// La scheda e' divisa in 3 pezzi renderizzati in punti diversi della pagina:
//   useVicine()      -> stato + logica (geoloc, ricerca, dettaglio, filtro categoria)
//   VicineControls   -> raggio + Avvia (nel pannello a sinistra)
//   VicineFiltri     -> chip filtro per categoria (al posto dei contatori)
//   VicineLista      -> elenco risultati + dettaglio (al posto della cronologia)
const RAGGI = [{ v: 1000, l: '1 km' }, { v: 2000, l: '2 km' }, { v: 5000, l: '5 km' }, { v: 10000, l: '10 km' }];
const fmtDist = (m) => (m == null ? '' : m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1)} km`);
const LAST_TTL = 72 * 3600 * 1000;   // l'ultima ricerca resta valida 72h

// Badge stato in archivio: cliente TIM / lead (NON TIM coperto) / gia' in archivio.
const ARCH = { tim: { t: 'TIM', c: '#3b82f6' }, lead: { t: 'LEAD', c: '#22c55e' }, archivio: { t: 'in arch.', c: '#94a3b8' } };
const archBadge = (a, big) => {
  if (!a || !a.trovato) return null;
  const m = ARCH[a.stato] || ARCH.archivio;
  const label = a.stato === 'lead' && a.tecnologia ? `LEAD · ${a.tecnologia}` : m.t;
  return <span style={{ fontSize: big ? 12 : 10.5, fontWeight: 800, padding: big ? '3px 10px' : '2px 7px', borderRadius: 999, color: '#fff', background: m.c, whiteSpace: 'nowrap' }}>{label}</span>;
};

// Filtri per forma societaria (dal nome): SRL / SPA, normali e puntate.
const FORME = [{ k: 'srl', l: 'SRL' }, { k: 'spa', l: 'SPA' }];
const RX_SRL = /\bS\.?\s?R\.?\s?L\.?\s?S?\b/i;
const RX_SPA = /\bS\.?\s?P\.?\s?A\.?\b|societ[aà].{0,40}per\s+azioni/i;
const grpDi = (it) => {
  const n = (it && it.nome) || '';
  if (RX_SRL.test(n)) return 'srl';
  if (RX_SPA.test(n)) return 'spa';
  return 'altro';
};

export function useVicine(adapter, agentId) {
  const [raggio, setRaggio] = useState(1000);
  const [items, setItems] = useState(null);   // null = mai cercato; aziende accumulate (con paginazione)
  const [busy, setBusy] = useState(false);     // ricerca iniziale in corso
  const [more, setMore] = useState(false);     // "carica altre" in corso
  const [next, setNext] = useState(null);      // token pagina successiva (null = fine)
  // Quante societa' il filtro 'solo con sito' sta tenendo fuori: nasconderle in silenzio
  // faceva sembrare la zona piu' povera di quello che e' (GI.L.C. a Cernusco, 06/10/2026).
  const [senzaSito, setSenzaSito] = useState(0);
  const [msg, setMsg] = useState('');
  const [aperto, setAperto] = useState(null);  // dettaglio attivita' selezionata
  const [gruppo, setGruppo] = useState('all'); // filtro categoria attivo (lato client)
  const [pos, setPos] = useState(null);        // ultima posizione usata (per "carica altre")
  const [savedTs, setSavedTs] = useState(null); // quando e' stata fatta l'ultima ricerca salvata

  // Ripristino dell'ultima ricerca (per-agente, snapshot a 72h): niente sparizione alla riapertura
  // e nessuna nuova chiamata a Google. Non e' un archivio permanente (Google vieta di conservare).
  const lsKey = 'vic:last:' + (agentId || 'anon');
  const saveLast = (itemsArg, posArg, nextArg) => {
    if (!agentId) return;
    const ts = Date.now();
    try { localStorage.setItem(lsKey, JSON.stringify({ pos: posArg, raggio, items: (itemsArg || []).slice(0, 200), next: nextArg || null, ts })); } catch (e) { /* no-op */ }
    setSavedTs(ts);
  };
  const svuota = () => {
    setItems(null); setNext(null); setAperto(null); setGruppo('all'); setSavedTs(null); setSenzaSito(0);
    try { localStorage.removeItem(lsKey); } catch (e) { /* no-op */ }
  };
  const restored = useRef(false);
  useEffect(() => {
    if (!agentId || restored.current) return;
    restored.current = true;
    try {
      const d = JSON.parse(localStorage.getItem(lsKey) || 'null');
      if (d && d.ts && (Date.now() - d.ts) < LAST_TTL && Array.isArray(d.items) && d.items.length) {
        setItems(d.items); setPos(d.pos || null); setNext(d.next || null); setSavedTs(d.ts);
        if (d.raggio) setRaggio(d.raggio);
      }
    } catch (e) { /* no-op */ }
  }, [agentId, lsKey]);

  // Aziende gia' analizzate (456/Dominio lanciato da qui): segno per-agente, cosi' nella lista
  // vedi subito quali hai gia' fatto anche se il match in archivio non e' ancora aggiornato.
  const [analizzate, setAnalizzate] = useState({});   // { placeId: ts }
  const analKey = 'vic:anal:' + (agentId || 'anon');
  const analLoaded = useRef(false);
  useEffect(() => {
    if (!agentId || analLoaded.current) return;
    analLoaded.current = true;
    try { const d = JSON.parse(localStorage.getItem(analKey) || '{}'); if (d && typeof d === 'object') setAnalizzate(d); } catch (e) { /* no-op */ }
  }, [agentId, analKey]);
  const segnaAnalizzata = (id) => {
    if (!id) return;
    setAnalizzate((prev) => {
      const next = { ...prev, [id]: Date.now() };
      const keys = Object.keys(next);
      if (keys.length > 800) { keys.sort((a, b) => next[a] - next[b]); keys.slice(0, keys.length - 800).forEach((k) => delete next[k]); }
      try { localStorage.setItem(analKey, JSON.stringify(next)); } catch (e) { /* no-op */ }
      return next;
    });
  };

  // Conteggio per il chip categoria (lato client, gratis).
  const contaGruppo = (k) => {
    if (!items) return 0;
    return k === 'all' ? items.length : items.filter(it => grpDi(it) === k).length;
  };

  const itemsFiltrati = useMemo(() => {
    if (!items) return items;
    return gruppo === 'all' ? items : items.filter(it => grpDi(it) === gruppo);
  }, [items, gruppo]);

  const trova = () => {
    if (!navigator.geolocation) { setMsg('Geolocalizzazione non supportata dal browser'); return; }
    if (window.isSecureContext === false) { setMsg('Per la posizione apri l\'app in https://'); return; }
    setBusy(true); setMsg('Localizzo…'); setItems(null); setAperto(null); setGruppo('all'); setNext(null); setSenzaSito(0);
    let best = null, done = false, wid = null, to = null;
    const stop = () => { if (wid != null) { try { navigator.geolocation.clearWatch(wid); } catch (e) {} } clearTimeout(to); };
    const cerca = async (p) => {
      setMsg('Cerco aziende vicine…'); setPos({ lat: p.coords.latitude, lon: p.coords.longitude });
      try {
        const r = await adapter.vicineTrova(p.coords.latitude, p.coords.longitude, raggio);
        setItems(r.items || []); setNext(r.next || null); setSenzaSito(Number(r.senza_sito) || 0);
        saveLast(r.items || [], { lat: p.coords.latitude, lon: p.coords.longitude }, r.next || null);
        setMsg((r.items || []).length ? '' : 'Nessuna azienda trovata nel raggio scelto.');
      } catch (e) { setItems([]); setMsg(e?.response?.data?.detail || 'Ricerca non riuscita.'); }
      finally { setBusy(false); }
    };
    wid = navigator.geolocation.watchPosition((p) => {
      if (!best || p.coords.accuracy < best.coords.accuracy) best = p;
      setMsg(`Localizzo… (±${Math.round(p.coords.accuracy)} m)`);
      if (!done && p.coords.accuracy <= 30) { done = true; stop(); cerca(p); }
    }, (err) => {
      if (done) return; done = true; stop(); setBusy(false);
      setMsg(err && err.code === 1 ? 'Posizione negata: attivala dalle Impostazioni del telefono.' : 'Posizione non disponibile, riprova.');
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
    to = setTimeout(() => { if (done) return; done = true; stop(); if (best) cerca(best); else { setBusy(false); setMsg('Posizione poco precisa: riprova all\'aperto.'); } }, 9000);
  };

  // Carica le prossime 20 aziende (1 credito) e le accoda, senza doppioni.
  const caricaAltre = async () => {
    if (!next || !pos || more) return;
    setMore(true);
    try {
      const r = await adapter.vicineTrova(pos.lat, pos.lon, raggio, next);
      const nuovi = r.items || [];
      const visti = new Set((items || []).map(x => x.id));
      const merged = [...(items || []), ...nuovi.filter(x => !visti.has(x.id))];
      setItems(merged);
      setNext(r.next || null);
      saveLast(merged, pos, r.next || null);
    } catch (e) { setMsg(e?.response?.data?.detail || 'Impossibile caricare altre.'); }
    finally { setMore(false); }
  };

  const apri = async (it) => {
    setAperto({ ...it, loading: true });
    try { const d = await adapter.vicineDettaglio(it.id); setAperto({ ...it, ...d, loading: false }); }
    catch (e) { setAperto({ ...it, loading: false, err: e?.response?.data?.detail || 'Dettaglio non disponibile' }); }
  };

  // Cerca aziende attorno a un punto SCELTO (lat/lon) invece che dalla geoloc: usato dalla mappa interattiva.
  const cercaCoord = async (lat, lon) => {
    setBusy(true); setMsg('Cerco aziende in zona…'); setItems(null); setAperto(null); setGruppo('all'); setNext(null); setSenzaSito(0);
    setPos({ lat, lon });
    try {
      const r = await adapter.vicineTrova(lat, lon, raggio);
      setItems(r.items || []); setNext(r.next || null); setSenzaSito(Number(r.senza_sito) || 0);
      saveLast(r.items || [], { lat, lon }, r.next || null);
      setMsg((r.items || []).length ? '' : 'Nessuna azienda trovata nel raggio scelto.');
    } catch (e) { setItems([]); setMsg(e?.response?.data?.detail || 'Ricerca non riuscita.'); }
    finally { setBusy(false); }
  };

  const reverseGeo = (adapter && adapter.topo && adapter.topo.reverseGeo) || null;
  return { raggio, setRaggio, items, busy, more, next, senzaSito, msg, aperto, setAperto, gruppo, setGruppo, pos,
           itemsFiltrati, contaGruppo, trova, caricaAltre, apri, cercaCoord, reverseGeo, savedTs, svuota,
           analizzate, segnaAnalizzata, FORME };
}

// ── Mappa per scegliere il PUNTO di partenza (Leaflet + OpenStreetMap, gratis) ──
const VIC_PIN = L.divIcon({ className: 'vic-pin', html: '<span></span>', iconSize: [22, 22], iconAnchor: [11, 11] });

function MapClick({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}
// Ricalcola la dimensione della mappa piu' volte: dentro un contenitore appena
// mostrato Leaflet spesso nasce a 0px -> tile nere/schermo nero senza questo.
function EnsureSize() {
  const map = useMap();
  useEffect(() => {
    const fix = () => { try { map.invalidateSize(); } catch (e) { /* no-op */ } };
    const ts = [0, 150, 350, 700].map((d) => setTimeout(fix, d));
    return () => ts.forEach(clearTimeout);
  }, [map]);
  return null;
}

// All'apertura: centra sulla mia posizione (o su Italia) UNA SOLA VOLTA. Se ri-scattasse
// a ogni render (initial/onAuto instabili) farebbe setView di continuo e la mappa non si sposta.
function RecenterOnOpen({ initial, onAuto }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const go = (lat, lon, z) => { try { map.invalidateSize(); map.setView([lat, lon], z); } catch (e) { /* no-op */ } };
    if (initial) { go(initial.lat, initial.lon, 15); return; }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => { go(p.coords.latitude, p.coords.longitude, 15); if (onAuto) onAuto(p.coords.latitude, p.coords.longitude); },
        () => {}, { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 });
    }
  }, [map, initial, onAuto]);
  return null;
}

// Inquadra tutto il cerchio quando cambia il raggio (o al primo punto scelto), non a ogni trascinamento.
function FitCircle({ pt, raggio }) {
  const map = useMap();
  const prev = useRef({ r: null, has: false });
  useEffect(() => {
    if (!pt) { prev.current = { r: raggio, has: false }; return; }
    const changedR = prev.current.r !== raggio;
    const justPicked = !prev.current.has;
    prev.current = { r: raggio, has: true };
    if (changedR || justPicked) {
      try {
        map.invalidateSize();
        if (map.getSize().y > 20) {   // solo se la mappa e' gia' dimensionata (evita zoom NaN -> schermo nero)
          map.fitBounds(L.circle([pt.lat, pt.lon], { radius: raggio }).getBounds(), { padding: [30, 30], maxZoom: 16 });
        }
      } catch (e) { /* no-op */ }
    }
  }, [map, pt, raggio]);
  return null;
}

export function VicineMapPicker({ vic, onClose }) {
  const { pos, raggio, setRaggio, cercaCoord, reverseGeo } = vic;
  const initialRef = useRef(pos ? { lat: pos.lat, lon: pos.lon } : null);   // stabile: calcolato una volta
  const initial = initialRef.current;
  const start = initial ? [initial.lat, initial.lon] : [42, 12.5];
  const [pt, setPt] = useState(initial);
  const [addr, setAddr] = useState('');
  const [geoMsg, setGeoMsg] = useState('');
  const mapRef = useRef(null);
  const geoTO = useRef(null);
  const geocode = (lat, lon) => {
    if (!reverseGeo) return;
    clearTimeout(geoTO.current);
    geoTO.current = setTimeout(async () => {
      try { const d = await reverseGeo(lat, lon); setAddr([d && d.via, d && d.comune].filter(Boolean).join(', ') || (d && d.display_name) || ''); }
      catch { setAddr(''); }
    }, 400);
  };
  const pick = (lat, lon) => { setPt({ lat, lon }); geocode(lat, lon); };
  const localizzami = () => {
    if (!navigator.geolocation) { setGeoMsg('Geolocalizzazione non supportata dal browser'); return; }
    if (window.isSecureContext === false) { setGeoMsg('Per la posizione apri l\'app in https://'); return; }
    setGeoMsg('Localizzo…');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const lat = p.coords.latitude, lon = p.coords.longitude;
        pick(lat, lon);
        try { if (mapRef.current) mapRef.current.setView([lat, lon], 16); } catch (e) { /* no-op */ }
        setGeoMsg('');
      },
      (err) => setGeoMsg(err && err.code === 1 ? 'Posizione negata: attivala dalle Impostazioni del telefono.' : 'Posizione non disponibile, riprova.'),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 });
  };
  const cerca = () => { if (!pt) return; cercaCoord(pt.lat, pt.lon); onClose(); };
  // Portal fuori dal pannello .tool (che ha backdrop-filter e intrappolerebbe il position:fixed).
  return createPortal((
    <div className="vic-map-full">
      <div className="vic-map-top">
        <div style={{ fontWeight: 800, fontSize: 15 }}>Scegli il punto di partenza</div>
        <button type="button" className="vic-map-x" onClick={onClose} aria-label="Chiudi">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>
      <div className="vic-map-canvas" style={{ position: 'relative' }}>
        <MapContainer center={start} zoom={initial ? 15 : 6} style={{ height: '100%', width: '100%' }} scrollWheelZoom
          ref={mapRef} attributionControl={false}>
          <AttributionControl prefix={false} />
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <EnsureSize />
          <MapClick onPick={pick} />
          <RecenterOnOpen initial={initial} onAuto={pick} />
          <FitCircle pt={pt} raggio={raggio} />
          {pt && <Circle center={[pt.lat, pt.lon]} radius={raggio} interactive={false}
            pathOptions={{ color: '#f97316', weight: 2, fillColor: '#f97316', fillOpacity: 0.12 }} />}
          {pt && <Marker position={[pt.lat, pt.lon]} icon={VIC_PIN} draggable
            eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); pick(ll.lat, ll.lng); } }} />}
        </MapContainer>
        <button type="button" onClick={localizzami} title="Localizzami" aria-label="Localizzami"
          style={{ position: 'absolute', left: 12, bottom: 12, zIndex: 1000, width: 44, height: 44,
            borderRadius: 999, border: 'none', cursor: 'pointer', color: '#fff',
            background: 'linear-gradient(180deg,#f97316,#c2560a)', boxShadow: '0 3px 10px rgba(0,0,0,.3)',
            display: 'grid', placeItems: 'center' }}>
          <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></svg>
        </button>
      </div>
      <div className="vic-map-bottom">
        <div className="foot" style={{ margin: 0 }}>
          {geoMsg || (pt ? (addr || `${pt.lat.toFixed(5)}, ${pt.lon.toFixed(5)}`) : 'Tocca la mappa per scegliere il punto (o usa il pulsante Localizzami in basso a sinistra).')}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {RAGGI.map((r) => (
            <button key={r.v} type="button" onClick={() => setRaggio(r.v)} style={{
              padding: '6px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: '1px solid ' + (raggio === r.v ? 'var(--accent-line)' : 'var(--line-2)'),
              background: raggio === r.v ? 'var(--accent-soft)' : 'var(--surface-2)',
              color: raggio === r.v ? 'var(--accent)' : 'var(--muted)',
            }}>{r.l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" style={{ flex: 1, background: 'var(--surface-2)', color: 'var(--ink)' }} onClick={onClose}>Annulla</button>
          <button className="btn" style={{ flex: 1, background: 'linear-gradient(180deg,#f97316,#c2560a)', color: '#fff' }} disabled={!pt} onClick={cerca}>Cerca qui</button>
        </div>
      </div>
    </div>
  ), document.querySelector('.pubdash') || document.body);
}

// Comandi (raggio + Avvia) — nel pannello a sinistra.
export function VicineControls({ vic, crediti }) {
  const { busy, msg, trova } = vic;
  const [mapOpen, setMapOpen] = useState(false);
  return (
    <div>
      <div className="field" style={{ alignItems: 'center' }}>
        <button type="button" onClick={() => setMapOpen(true)} disabled={busy}
          style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 9, textAlign: 'left',
            background: 'var(--input-bg)', border: '1px solid var(--line-2)', borderRadius: 12,
            padding: '12px 14px', color: 'var(--muted)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto' }}><path d="M12 21s-7-6-7-11a7 7 0 0 1 14 0c0 5-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Scegli il punto sulla mappa</span>
        </button>
        <button data-tour="vic-btn" className="btn" onClick={trova} disabled={busy}
          title={crediti ? `Crediti Vicine rimasti questo mese: ${crediti.residui} su ${crediti.limite}` : undefined}
          style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', gap: 9, paddingLeft: 18, paddingRight: 18 }}>
          <span>{busy ? 'Attendere…' : 'Avvia'}</span>
          {crediti && (
            <span style={{ fontSize: 12, fontWeight: 800, padding: '2px 8px', borderRadius: 999, lineHeight: 1.4, whiteSpace: 'nowrap',
              background: 'rgba(255,255,255,.22)', color: crediti.residui > 0 ? '#fff' : '#ffd7d7' }}>
              {crediti.residui}/{crediti.limite}
            </span>
          )}
        </button>
      </div>
      {msg && <div className="foot" style={{ marginTop: 8 }}>{msg}</div>}
      {mapOpen && <VicineMapPicker vic={vic} onClose={() => setMapOpen(false)} />}
    </div>
  );
}

// Filtri categoria (chip) — stessa grafica dei contatori (.statbar/.schip), filtro lato client (gratis).
export function VicineFiltri({ vic }) {
  const { gruppo, setGruppo, contaGruppo, items, FORME: F } = vic;
  if (!items || !items.length) return null;
  const chip = (k, l) => (
    <button key={k} type="button" className={`schip vfilt${gruppo === k ? ' on' : ''}`} onClick={() => setGruppo(k)}>
      <div className="k">{contaGruppo(k)}</div>
      <div className="l">{l}</div>
    </button>
  );
  return (
    <div className="statbar vfiltbar">
      {chip('all', 'Tutte')}
      {F.map(g => chip(g.k, g.l))}
    </div>
  );
}

// Elenco risultati + dettaglio — al posto della cronologia.
export function VicineLista({ vic, onVerifica456, onDominio }) {
  const { items, itemsFiltrati, aperto, setAperto, apri, busy, next, more, caricaAltre, gruppo, analizzate, segnaAnalizzata } = vic;

  if (items == null) {
    return <div style={{ padding: '30px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
      {busy ? 'Ricerca in corso…' : 'Avvia una ricerca: le aziende vicine compariranno qui.'}
    </div>;
  }
  const list = itemsFiltrati || [];
  return (
    <>
      {list.length === 0 ? (
        <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>Nessuna azienda in questa categoria.</div>
      ) : (
        <div>
          {list.map((it, i) => {
            const fatta = analizzate && analizzate[it.id];
            return (
            <div key={it.id || i} onClick={() => apri(it)} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer',
              borderTop: i ? '1px solid var(--line-2)' : 'none', opacity: fatta ? 0.62 : 1,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {fatta && <span style={{ color: '#16a34a', marginRight: 6 }}>✓</span>}{it.nome || '—'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {it.categoria ? `${it.categoria} · ` : ''}{it.indirizzo || ''}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flex: '0 0 auto' }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--accent)' }}>{fmtDist(it.distanza)}</span>
                {fatta
                  ? <span style={{ fontSize: 10.5, fontWeight: 800, padding: '2px 7px', borderRadius: 999, color: '#16a34a', background: 'rgba(34,197,94,.16)', whiteSpace: 'nowrap' }}>già analizzata</span>
                  : archBadge(it.archivio)}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {next && gruppo === 'all' && (
        <div style={{ padding: '12px 14px', textAlign: 'center' }}>
          <button className="btn ghost" onClick={caricaAltre} disabled={more} style={{ width: '100%' }}>
            {more ? 'Carico…' : 'Carica altre 20 (1 credito)'}
          </button>
        </div>
      )}

      {aperto && (
        <div className="scrim" onClick={() => setAperto(null)}>
          <div className="modal" style={{ textAlign: 'left', width: 'min(400px,94vw)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 800, fontSize: 16, color: 'var(--ink)', marginBottom: 2 }}>{aperto.nome || '—'}</div>
            {aperto.categoria && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 8 }}>{aperto.categoria}{aperto.distanza != null ? ` · ${fmtDist(aperto.distanza)}` : ''}</div>}
            {!aperto.loading && aperto.archivio && aperto.archivio.trovato && (
              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                {archBadge(aperto.archivio, true)}
                <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                  già in archivio{aperto.archivio.risultato ? ` · ${aperto.archivio.risultato}` : ''}{aperto.archivio.lavorato ? ' · lavorata' : ''}
                </span>
              </div>
            )}
            {!aperto.loading && aperto.archivio && !aperto.archivio.trovato && (
              <div style={{ marginBottom: 10, fontSize: 12.5, color: '#16a34a', fontWeight: 700 }}>Nuova (non in archivio)</div>
            )}
            {aperto.loading ? (
              <div style={{ color: 'var(--muted)', fontSize: 13, padding: '10px 0' }}>Carico i dettagli…</div>
            ) : aperto.err ? (
              <div style={{ color: '#ef4444', fontSize: 13, padding: '10px 0' }}>{aperto.err}</div>
            ) : (
              <>
                <div style={{ fontSize: 13, display: 'grid', gap: 4, marginBottom: 12, color: 'var(--muted)' }}>
                  <div><b style={{ color: 'var(--ink)' }}>Indirizzo:</b> {aperto.indirizzo || '—'}</div>
                  <div><b style={{ color: 'var(--ink)' }}>Telefono:</b> {aperto.telefono || '—'}</div>
                  <div><b style={{ color: 'var(--ink)' }}>Sito:</b> {aperto.sito ? <a href={aperto.sito} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>{aperto.sito}</a> : '—'}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn" style={{ flex: 1, minWidth: 130, color: '#fff', background: 'linear-gradient(180deg,var(--c456),var(--c456b))' }} disabled={!aperto.telefono}
                    onClick={() => { segnaAnalizzata && segnaAnalizzata(aperto.id); onVerifica456 && onVerifica456(aperto.telefono); setAperto(null); }}>Verifica 456</button>
                  <button className="btn" style={{ flex: 1, minWidth: 130, color: '#fff', background: 'linear-gradient(180deg,#22c55e,#16a34a)' }} disabled={!aperto.sito}
                    onClick={() => { segnaAnalizzata && segnaAnalizzata(aperto.id);
                      /* l'indirizzo di Google viaggia col dominio: serve da RIPIEGO se il
                         sito non ne pubblica uno (caso OPPO). Comanda comunque il sito. */
                      onDominio && onDominio(aperto.sito, aperto.indirizzo_comp);
                      setAperto(null); }}>Verifica dominio</button>
                </div>
                {!aperto.telefono && !aperto.sito && <div className="foot" style={{ marginTop: 8 }}>Nessun telefono o sito per questa attivita'.</div>}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
